import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthLayout } from './Signup';

/** Landing page for magic-link emails. The actual token redemption is a
 * POST request fired on mount — that way Gmail's GET-based link
 * prefetcher can't burn the single-use token before the human gets
 * here. On success the API sets the session cookie and we navigate to
 * the returned landing path. */
export default function MagicConsume() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setErr('Missing token. Open the link from your email again, or request a new one.');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/auth/magic/consume', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        if (!res.ok) {
          // 400 'invalid' (expired / used / unknown). Bounce to /login with
          // the existing error rendering so users see a clear next step.
          if (!cancelled) nav('/login?error=invalid', { replace: true });
          return;
        }
        const body = await res.json() as { redirect_to: string };
        if (!cancelled) nav(body.redirect_to || '/tenants', { replace: true });
      } catch (e: any) {
        if (!cancelled) setErr('Network error. Try the link again, or request a new one.');
      }
    })();
    return () => { cancelled = true; };
  }, [token, nav]);

  return (
    <AuthLayout
      title={err ? 'Sign-in failed' : 'Signing you in…'}
      sub={err || 'Hold tight — verifying your link.'}
    >
      {!err && (
        <div className="flex justify-center py-2">
          <div className="w-6 h-6 border-2 border-accent border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      {err && (
        <p className="text-xs text-dim mt-2">
          <a href="/login" className="text-accent hover:underline">Request a new link</a>
        </p>
      )}
    </AuthLayout>
  );
}
